import Typography from "@mui/material/Typography";
import { Avatar, Box, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { generateGtmEvent, timeDifference } from "../../../Helpers";
import React, { useContext, useEffect, useState } from "react";
import { StudentDetailedModal } from "../insights/Summary/componentUtils/StudentDetailedModal";
import { InsightsProgressFilterContext } from "../../../context/InsightsProgressFilterContext";
import {
  CourseStructureCtx,
  InsightsCtx,
  InsightsFilterCtx,
  NotificationCategory,
} from "../../../interfaces";
import { UserNotification } from "@backend-types";
import { NotificationType } from "../../../enums";
import axios from "../../../services/axios";
import { InsightsContext } from "../../../context/InsightsContext";
import { CourseStructureContext } from "../../../context/CourseStructureContext";

const useStyles = makeStyles((theme) => ({
  highlight: {
    font: "normal normal bold 16px/24px Roboto",
    letterSpacing: "0px",
    color: "#000000DE",
  },
  listBox: {
    padding: "8px",
    "&:hover": {
      background: "#F5F5F5 0% 0% no-repeat padding-box",
      borderRadius: "5px",
    },
    cursor: "pointer",
  },
}));

interface NotificationListItemProps {
  notification: UserNotification;

  notificationCategory: NotificationCategory;
}

const NOTIFICATION_TYPE = {
  Incorrect_Mastery: "incorrectly answered",
  New_Course_Enrolled: "New_Course_Enrolled",
};

export const NotificationListItem = (props: NotificationListItemProps) => {
  const classes = useStyles();
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const InsightProgressFilterContext = useContext(
    InsightsProgressFilterContext
  ) as InsightsFilterCtx;
  const { notification, notificationCategory } = props;
  let NotificationMetadata = notification.metadata;
  const [openStudentDetailModal, setOpenStudentDetailModal] = useState(false);
  const [studentPercentage, setStudentPercentage] = useState(0);
  const [notificationCategoryWhiteSpace, setNotificationCategoryWhiteSpace] =
    useState("nowrap");

  // get course context of list item
  const courseId = NotificationMetadata.sourceClassId
    ? insightsCtx.getCourseIdFromClassID(NotificationMetadata.sourceClassId)
    : null;
  const currentCourseModuleFilters = courseStructureCtx.getModuleFiltersInCourse(
    courseId ?? -1
  );
  const currentCourseMetadata = courseStructureCtx.getMetadataInCourse(courseId ?? -1);
  const currentCourseModuleIds = currentCourseModuleFilters?.map((item) => item.id);

  const GetStudentSummary = async () => {
    let courseFilter = InsightProgressFilterContext.selectedCourseFilter ?? {
      moduleIds: currentCourseModuleIds,
    };
    if (!courseFilter) return;

    try {
      let response = await axios.post(`/insights/student-summary`, {
        type: "detailed",
        students: [{ id: NotificationMetadata.sourceId }],
        courseFilter: courseFilter,
        classIds: [NotificationMetadata.sourceClassId],
      });
      if (response.data) {
        setStudentPercentage(response.data[0].percentage);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleNotificationClick = async () => {
    setOpenStudentDetailModal(true);
    InsightProgressFilterContext.setFilters([
      {
        id: NotificationMetadata.targetId!,
        name: NotificationMetadata.targetTitle!,
        type: "target",
      },
    ]);
    await GetStudentSummary();
    generateGtmEvent({
      event: "modal",
      action: "open",
      name: "onclick",
      type: "click",
      region: "main content",
      section: notificationCategory.name,
      text: `${NotificationMetadata.sourceName} ${
        NOTIFICATION_TYPE[notification.type as NotificationType]
      } ${NotificationMetadata.targetTitle} ${NotificationMetadata.questionTitle} ${
        NotificationMetadata.attempts === 2 ? "twice" : "2 or more times."
      }`,
    });
  };

  const handleStudentDetailModalClose = () => {
    setOpenStudentDetailModal(false);
    InsightProgressFilterContext.setFilters([]);
  };

  useEffect(() => {
    if (
      notificationCategory.whiteSpace === "nowrap" &&
      NotificationMetadata.targetTitle &&
      NotificationMetadata.targetTitle?.length < 20
    ) {
      console.log(
        NotificationMetadata.targetTitle,
        "target title",
        NotificationMetadata.targetTitle?.length,
        notificationCategory.whiteSpace
      );
      setNotificationCategoryWhiteSpace("break-spaces");
    } else {
      setNotificationCategoryWhiteSpace(notificationCategory.whiteSpace);
    }
  }, [notificationCategory.whiteSpace, NotificationMetadata.targetTitle]);

  return (
    <>
      <Box
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleNotificationClick();
          }
        }}
        onClick={() => {
          handleNotificationClick();
        }}
        className={classes.listBox}>
        <ListItem
          alignItems="flex-start"
          sx={{
            paddingLeft: "0px !important",
            paddingRight: "0px !important",
            gap: "12px",
          }}>
          <ListItemAvatar>
            <Avatar
              sx={{
                backgroundColor: "#E2F7FF",
                height: "40px",
                width: "40px",
                marginRight: "4px",
              }}>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: "500",
                  fontSize: "1em",
                  fontFamily: "proxima-soft",
                  letterSpacing: "0.12px",
                }}>
                {NotificationMetadata.sourceName
                  ? NotificationMetadata.sourceName[0] +
                    NotificationMetadata.sourceName[
                      NotificationMetadata.sourceName.indexOf(" ") + 1
                    ]
                  : ""}
              </Typography>
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            sx={{ marginTop: "0px !important", marginBottom: "0px !important" }}
            primary={
              <p style={{ display: "inline" }}>
                <Typography component={"span"} className={classes.highlight}>
                  {NotificationMetadata.sourceName}
                </Typography>
                {` ${NOTIFICATION_TYPE[notification.type as NotificationType]}`}
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: notificationCategoryWhiteSpace,
                    ...(notificationCategoryWhiteSpace === "break-spaces" && {
                      display: "inherit",
                    }),
                  }}
                  className={classes.highlight}>
                  {` ${NotificationMetadata.targetTitle} `}
                </Typography>
                {`${NotificationMetadata.questionTitle} ${
                  NotificationMetadata.attempts === 2
                    ? "two or more times."
                    : "two or more times."
                }`}
              </p>
            }
            secondary={
              <Typography
                sx={{
                  display: "inline",
                  font: "normal normal normal 14px/20px Roboto",
                  letterSpacing: "0px",
                  color: "#00000099",
                }}
                component="span"
                variant="body2">
                {timeDifference(
                  new Date().getTime(),
                  new Date(notification.created_at).getTime()
                )}
              </Typography>
            }
          />
        </ListItem>
      </Box>
      <StudentDetailedModal
        defaultModuleIds={currentCourseModuleIds ?? []}
        handleClose={handleStudentDetailModalClose}
        open={openStudentDetailModal}
        studentName={NotificationMetadata.sourceName!}
        progress={studentPercentage}
        studentEmail={NotificationMetadata.sourceEmail!}
        studentId={NotificationMetadata.sourceId!}
        classId={NotificationMetadata.sourceClassId!}
        courseMetadata={currentCourseMetadata}
      />
    </>
  );
};
